import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome to EHP, ${res.user.name}!`);
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <section className="section-pad">
      <div className="container-xl flex justify-center">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="register-card">
          <div className="mb-6 text-center">
            <img src="/ehp-logo.jpg" alt="EHP" className="mx-auto h-14 w-14 rounded-full bg-white object-contain ring-1 ring-border" />
            <h1 className="mt-4 font-serif text-3xl">Create your account</h1>
            <p className="mt-2 text-sm text-muted-foreground">Join EHP for a natural wellness journey</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <input required placeholder="Full name" className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input" />
            <input required type="email" placeholder="Email" className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
            <div className="relative">
              <input
                required
                type={showPassword ? "text" : "password"}
                placeholder="Password (min 6 characters)"
                className="input-field pr-11"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="register-password-toggle"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="register-submit-button">
              {loading ? "Creating..." : (<>Create Account <UserPlus className="h-4 w-4" /></>)}
            </button>
          </form>
          <GoogleSignInButton testId="register-google-button" />
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="font-600 text-primary hover:underline" data-testid="register-to-login">Sign in</Link>
          </p>
        </div>
      </div>
    </section>
  );
}
